// SPDX-License-Identifier: MIT
// /compte — "Mon espace" : the user's personal space. Ported verbatim from the
// operator-approved mockup /__mock/mon-espace-v2/ (the mockup IS the component),
// wired to real data : getEntitlement (idées gauge + plan), listProjects (grid),
// getUserAiProvider (BYOAI status). Honest `live`/`bientôt` badges — checkout
// (Mollie/Wero) isn't built yet, so every purchase/billing/export/2FA surface is
// marked "bientôt" rather than faking it. Page-specific copy is a local fr/en
// dict (this is a large standalone page ; keeping ~50 strings out of the global
// bundle keeps the diff contained, like exemples.ts).
import { renderHeader } from '../components/header';
import { renderFooter } from '../components/footer';
import { escapeHtml } from '../lib/escape';
import { getEntitlement } from '../entitlements/api';
import { listPayments } from '../billing/api';
import { cloudEnabled, subscribeCloud, cancelCloud } from '../checkout/cloud';
import { listProjects } from '../projects/api';
import { getUserAiProvider } from '../auth/ai-provider';
import { wireCheckoutCta } from '../checkout/cta';
import type { Locale } from '../i18n/detect';
import type { Session } from '@supabase/supabase-js';

export interface ComptePageOptions {
  session: Session;
  locale?: Locale;
  onSignOut?: () => void | Promise<void>;
  // Set when the visitor just returned from a successful Mollie checkout
  // (/compte?checkout=done) → render a success banner; the gauge reflects the
  // freshly-credited ideas on mount (getEntitlement fetches fresh).
  justPurchased?: boolean;
  // Set when the visitor just returned from a Cloud subscription checkout
  // (/compte?checkout=cloud) → render the Cloud-activated banner.
  justSubscribedCloud?: boolean;
}

const STR = {
  fr: {
    heroPlan: 'Plan Découverte', memberSince: 'membre depuis', editProfile: 'Modifier le profil', signOut: 'Se déconnecter',
    gaugeUnit: 'idées prêtes à magnifier', gaugeCap: 'chaque idée devient un projet complet en un clic 🪄 · 14 jours en ligne offerts par idée',
    legOffered: 'offertes', legBought: 'achetées', legUsed: 'utilisées',
    navOffers: 'Idées & offres', navProjects: 'Mes projets', navConn: 'Connexions', navBilling: 'Facturation', navSec: 'Sécurité', navPrefs: 'Préférences', navData: 'Données', navHelp: 'Aide',
    offersH: "Plus d'idées, ou ton app en ligne", offersSub: "l'idée est l'unité — achète à l'usage, ou possède Genesis à vie",
    packsName: "Packs d'idées — Web", packsTag: 'inférence incluse · paie à l’usage', packsCta: 'Acheter des idées',
    packsLi: ['3 idées magnifiées (sur notre IA)', 'crédits sans expiration', '14 jours en ligne par idée'],
    deskName: 'Genesis Desktop', deskUnit: 'à vie', deskTag: 'idées illimitées ∞ · BYOAI · local', deskCta: 'Passer Desktop',
    deskLi: ['Idées illimitées sur ton Claude', 'Achat unique, à vie', '🎓 étudiants 29€ · 💛 Pro 99€'],
    cloudName: 'Cloud — hébergement', cloudUnit: '/mois', cloudTag: 'ton app en ligne 24/7', cloudCta: 'Activer le Cloud',
    cloudLi: ['En ligne + domaine perso', 'Auto-deploy + persistance', 'Sauvegardes'],
    payNote: 'Paiement souverain (Mollie · Wero)', payEnt: 'entreprise 🇫🇷 sur devis',
    projH: 'Mes projets', projLoading: 'chargement…', projEmpty: 'Aucun projet encore — magnifie ta première idée ✦', projCount: (n: number) => `${n} projet${n>1?'s':''} magnifié${n>1?'s':''}`, openAtelier: "→ Ouvrir l'Atelier",
    connH: 'Connexions', connSub: 'ton IA, ton Claude, tes dépôts',
    byoaiT: 'IA connectée (BYOAI)', byoaiNone: 'aucune clé connectée — IA incluse, ou branche ta clé', byoaiSet: (k: string)=>`clé connectée (${k})`, connect: 'Connecter',
    mcpT: 'Connecter mon Claude (MCP)', mcpD: 'relie Claude Desktop/Code à ton espace Genesis', configure: 'Configurer',
    forgejoT: 'Dépôts Forgejo', forgejoD: (n: number)=>`${n} projet${n>1?'s':''} poussé${n>1?'s':''} — code versionné, prêt à cloner`, forgejoNone: 'aucun dépôt encore — pousse un projet depuis l’Atelier', see: 'Voir',
    billH: 'Facturation', billSub: 'tes achats & reçus — paiement souverain Mollie · Wero (EU)',
    billHist: 'Historique des achats', billLoading: 'chargement…', billEmpty: "aucun achat pour l'instant", billCloud: 'Abonnement Cloud', billCloudD: 'inactif — gère-le ici une fois activé',
    cloudOk: 'Cloud activé — ton hébergement est en route 🎉', cloudActive: 'Cloud actif', cloudUntilLabel: "jusqu'au", resilier: 'Résilier', resilierConfirm: 'Résilier ton abonnement Cloud ? Il reste actif jusqu’à la fin de la période payée.',
    purchaseOk: 'Paiement reçu — tes idées sont créditées 🎉', dismiss: 'Fermer',
    skuPackWeb: 'Pack Web — 3 idées', skuPackTerminal: 'Pack Terminal — 5 idées', skuDesktop: 'Genesis Desktop à vie', statusPaid: 'payé',
    secH: 'Sécurité', secSub: 'accès & sessions', secMagic: 'Connexion par lien magique', secMagicD: (e: string)=>`${e} — sans mot de passe`, changeEmail: "Changer d'email",
    secSess: 'Sessions actives', secSessD: 'ce navigateur · dernière connexion aujourd’hui', signOutAll: 'Déconnecter partout', sec2fa: 'Double authentification (2FA)', sec2faD: 'renforce ton compte',
    prefsH: 'Préférences', prefLang: 'Langue', prefLangD: 'interface', prefTheme: 'Thème', prefThemeD: 'clair (carnet)', themeSoon: 'sombre bientôt', prefNotif: 'Notifications email', prefNotifD: 'nouveautés, expiration des previews',
    dataH: 'Données & confidentialité', dataSub: "tes données t'appartiennent (RGPD)", dataExport: 'Exporter mes données', dataExportD: 'projets, idées, fiches — au format JSON/zip', dataDelete: 'Supprimer mon compte', dataDeleteD: 'efface définitivement compte + projets + données',
    helpH: 'Aide & Entreprise', helpGuide: 'Guide & exemples', helpGuideD: 'comment magnifier une idée, livrer, héberger', open: 'Ouvrir', helpSupport: 'Support', helpSupportD: 'une question, un souci ?', contact: 'Contacter', helpEnt: 'Entreprise 🇫🇷 — IA souveraine', helpEntD: 'déploiement sur-mesure, sur devis', talk: 'Nous parler',
    foot: "Genesis · 1 idée offerte à l'inscription · l'idée est l'unité (packs) · le Cloud garde tes apps en ligne · BYOAI absolu",
    bientot: 'bientôt', live: 'live',
  },
  en: {
    heroPlan: 'Discovery plan', memberSince: 'member since', editProfile: 'Edit profile', signOut: 'Sign out',
    gaugeUnit: 'ideas ready to magnify', gaugeCap: 'each idea becomes a full project in one click 🪄 · 14 days online free per idea',
    legOffered: 'offered', legBought: 'purchased', legUsed: 'used',
    navOffers: 'Ideas & plans', navProjects: 'My projects', navConn: 'Connections', navBilling: 'Billing', navSec: 'Security', navPrefs: 'Preferences', navData: 'Data', navHelp: 'Help',
    offersH: 'More ideas, or your app online', offersSub: 'the idea is the unit — pay as you go, or own Genesis for life',
    packsName: 'Idea packs — Web', packsTag: 'inference included · pay as you go', packsCta: 'Buy ideas',
    packsLi: ['3 magnified ideas (on our AI)', 'credits never expire', '14 days online per idea'],
    deskName: 'Genesis Desktop', deskUnit: 'for life', deskTag: 'unlimited ideas ∞ · BYOAI · local', deskCta: 'Get Desktop',
    deskLi: ['Unlimited ideas on your Claude', 'One-time, for life', '🎓 students 29€ · 💛 Pro 99€'],
    cloudName: 'Cloud — hosting', cloudUnit: '/month', cloudTag: 'your app online 24/7', cloudCta: 'Enable Cloud',
    cloudLi: ['Online + custom domain', 'Auto-deploy + persistence', 'Backups'],
    payNote: 'Sovereign payment (Mollie · Wero)', payEnt: 'enterprise 🇫🇷 on quote',
    projH: 'My projects', projLoading: 'loading…', projEmpty: 'No project yet — magnify your first idea ✦', projCount: (n: number) => `${n} project${n>1?'s':''} magnified`, openAtelier: '→ Open the Studio',
    connH: 'Connections', connSub: 'your AI, your Claude, your repos',
    byoaiT: 'Connected AI (BYOAI)', byoaiNone: 'no key connected — AI included, or plug in your key', byoaiSet: (k: string)=>`key connected (${k})`, connect: 'Connect',
    mcpT: 'Connect my Claude (MCP)', mcpD: 'link Claude Desktop/Code to your Genesis space', configure: 'Configure',
    forgejoT: 'Forgejo repos', forgejoD: (n: number)=>`${n} project${n>1?'s':''} pushed — versioned, ready to clone`, forgejoNone: 'no repo yet — push a project from the Studio', see: 'View',
    billH: 'Billing', billSub: 'your purchases & receipts — sovereign payment Mollie · Wero (EU)',
    billHist: 'Purchase history', billLoading: 'loading…', billEmpty: 'no purchases yet', billCloud: 'Cloud subscription', billCloudD: 'inactive — manage it here once active',
    cloudOk: 'Cloud activated — your hosting is on its way 🎉', cloudActive: 'Cloud active', cloudUntilLabel: 'until', resilier: 'Cancel', resilierConfirm: 'Cancel your Cloud subscription? It stays active until the end of the paid period.',
    purchaseOk: 'Payment received — your ideas are credited 🎉', dismiss: 'Dismiss',
    skuPackWeb: 'Web pack — 3 ideas', skuPackTerminal: 'Terminal pack — 5 ideas', skuDesktop: 'Genesis Desktop for life', statusPaid: 'paid',
    secH: 'Security', secSub: 'access & sessions', secMagic: 'Magic-link sign-in', secMagicD: (e: string)=>`${e} — passwordless`, changeEmail: 'Change email',
    secSess: 'Active sessions', secSessD: 'this browser · last sign-in today', signOutAll: 'Sign out everywhere', sec2fa: 'Two-factor (2FA)', sec2faD: 'harden your account',
    prefsH: 'Preferences', prefLang: 'Language', prefLangD: 'interface', prefTheme: 'Theme', prefThemeD: 'light (carnet)', themeSoon: 'dark soon', prefNotif: 'Email notifications', prefNotifD: 'news, preview expiry',
    dataH: 'Data & privacy', dataSub: 'your data is yours (GDPR)', dataExport: 'Export my data', dataExportD: 'projects, ideas, sheets — JSON/zip', dataDelete: 'Delete my account', dataDeleteD: 'permanently erases account + projects + data',
    helpH: 'Help & Enterprise', helpGuide: 'Guide & examples', helpGuideD: 'how to magnify an idea, ship, host', open: 'Open', helpSupport: 'Support', helpSupportD: 'a question, an issue?', contact: 'Contact', helpEnt: 'Enterprise 🇫🇷 — sovereign AI', helpEntD: 'bespoke deployment, on quote', talk: 'Talk to us',
    foot: 'Genesis · 1 idea offered on signup · the idea is the unit (packs) · Cloud keeps your apps online · absolute BYOAI',
    bientot: 'soon', live: 'live',
  },
} as const;

function monthYear(iso: string | undefined, locale: Locale): string {
  if (!iso) return '';
  try { return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(new Date(iso)); }
  catch { return ''; }
}

export function renderComptePage(opts: ComptePageOptions): DocumentFragment {
  const locale = opts.locale ?? 'fr';
  const S = STR[locale === 'en' ? 'en' : 'fr'];
  const email = opts.session.user?.email ?? '';
  const initial = (email.charAt(0) || '✦').toUpperCase();
  const since = monthYear(opts.session.user?.created_at, locale);

  const frag = document.createDocumentFragment();
  frag.appendChild(renderHeader({ locale, activePath: '/compte', user: { email }, onSignOut: opts.onSignOut, ideasRemaining: null }));

  const main = document.createElement('main');
  main.className = 'mon-espace';
  main.setAttribute('data-component', 'compte-page');

  const li = (items: readonly string[]) => items.map((x) => `<li>${escapeHtml(x)}</li>`).join('');
  main.innerHTML = `
    ${(opts.justPurchased || opts.justSubscribedCloud) ? `<div class="me-banner" data-component="purchase-banner" role="status">✓ ${escapeHtml(opts.justSubscribedCloud ? S.cloudOk : S.purchaseOk)}<button type="button" class="me-banner__x" data-component="purchase-banner-x" aria-label="${escapeHtml(S.dismiss)}">×</button></div>` : ''}
    <div class="me-hero">
      <section class="ident">
        <div class="ident__row">
          <span class="avatar">${escapeHtml(initial)}</span>
          <span class="ident__who"><b>${escapeHtml(email.split('@')[0] || 'toi')}</b><span>${escapeHtml(email)}</span></span>
        </div>
        <div class="ident__meta">
          <span class="chip chip--plan" data-component="plan-chip">${escapeHtml(S.heroPlan)}</span>
          ${since ? `<span class="chip">${escapeHtml(S.memberSince)} ${escapeHtml(since)}</span>` : ''}
        </div>
        <div class="ident__actions">
          <span class="miniseg" data-component="lang-seg"><button data-lang="fr" aria-pressed="${locale !== 'en'}">FR</button><button data-lang="en" aria-pressed="${locale === 'en'}">EN</button></span>
          ${opts.onSignOut ? `<button class="minibtn" data-component="signout">${escapeHtml(S.signOut)}</button>` : ''}
        </div>
      </section>

      <section class="gauge" aria-label="${escapeHtml(S.gaugeUnit)}">
        <div class="gauge__top"><span class="gauge__num" data-component="gauge-num">—</span><span class="gauge__unit">${escapeHtml(S.gaugeUnit)}</span></div>
        <div class="gauge__cap">${escapeHtml(S.gaugeCap)}</div>
        <div class="gauge__track"><div class="gauge__fill" data-component="gauge-fill" style="--target:0%"></div></div>
        <div class="gauge__legend">
          <span><i class="dot" style="background:var(--amber)"></i><b data-component="leg-offered">—</b> ${escapeHtml(S.legOffered)}</span>
          <span><i class="dot" style="background:var(--orange)"></i><b data-component="leg-bought">—</b> ${escapeHtml(S.legBought)}</span>
          <span><i class="dot" style="background:#d9cdb3"></i><b data-component="leg-used">—</b> ${escapeHtml(S.legUsed)}</span>
        </div>
      </section>
    </div>

    <div class="me-layout">
      <nav class="secnav" aria-label="${escapeHtml(S.navOffers)}">
        <a href="#me-offres">${escapeHtml(S.navOffers)}</a>
        <a href="#me-projets">${escapeHtml(S.navProjects)}</a>
        <a href="#me-conn">${escapeHtml(S.navConn)}</a>
        <a href="#me-bill">${escapeHtml(S.navBilling)}</a>
        <a href="#me-sec">${escapeHtml(S.navSec)}</a>
        <a href="#me-prefs">${escapeHtml(S.navPrefs)}</a>
        <a href="#me-data">${escapeHtml(S.navData)}</a>
        <a href="#me-help">${escapeHtml(S.navHelp)}</a>
      </nav>

      <div class="content">
        <section class="block" id="me-offres">
          <h2 class="block__h">${escapeHtml(S.offersH)}</h2>
          <p class="block__sub"><em>${escapeHtml(S.offersSub)}</em></p>
          <div class="offers">
            <article class="offer offer--packs"><span class="offer__glow"></span>
              <span class="offer__icon">✦</span><h3 class="offer__name">${escapeHtml(S.packsName)}</h3>
              <div class="offer__price">10€<small> / 3</small></div><p class="offer__tag">${escapeHtml(S.packsTag)}</p>
              <ul class="offer__list">${li(S.packsLi)}</ul><button type="button" class="cta" data-checkout="pack_web">${escapeHtml(S.packsCta)}</button></article>
            <article class="offer offer--desktop"><span class="offer__glow"></span>
              <span class="offer__icon">💻</span><h3 class="offer__name">${escapeHtml(S.deskName)}</h3>
              <div class="offer__price">49€<small> ${escapeHtml(S.deskUnit)}</small></div><p class="offer__tag">${escapeHtml(S.deskTag)}</p>
              <ul class="offer__list">${li(S.deskLi)}</ul><button type="button" class="cta" data-checkout="desktop_lifetime">${escapeHtml(S.deskCta)}</button></article>
            <article class="offer offer--cloud"><span class="offer__glow"></span>
              <span class="offer__icon">☁️</span><h3 class="offer__name">${escapeHtml(S.cloudName)}</h3>
              <div class="offer__price">5€<small> ${escapeHtml(S.cloudUnit)}</small></div><p class="offer__tag">${escapeHtml(S.cloudTag)}</p>
              <ul class="offer__list">${li(S.cloudLi)}</ul>${cloudEnabled() ? `<button type="button" class="cta" data-checkout-cloud>${escapeHtml(S.cloudCta)}</button>` : `<a class="cta" data-nav href="/tarifs">${escapeHtml(S.cloudCta)}</a>`}</article>
          </div>
          <p class="paynote">💳 ${escapeHtml(S.payNote)} <span class="bientot">${escapeHtml(S.bientot)}</span> — ${escapeHtml(S.payEnt)}</p>
        </section>

        <section class="block" id="me-projets">
          <h2 class="block__h">${escapeHtml(S.projH)} <span class="live">${escapeHtml(S.live)}</span></h2>
          <p class="block__sub" data-component="proj-count">${escapeHtml(S.projLoading)}</p>
          <div class="projgrid" data-component="proj-grid"></div>
          <div style="margin-top:14px"><a class="minibtn" data-nav href="/welcome">${escapeHtml(S.openAtelier)}</a></div>
        </section>

        <section class="block" id="me-conn">
          <h2 class="block__h">${escapeHtml(S.connH)}</h2>
          <p class="block__sub">${escapeHtml(S.connSub)}</p>
          <div class="row"><span class="row__ic">🤖</span><div class="row__main"><b>${escapeHtml(S.byoaiT)} <span class="live">${escapeHtml(S.live)}</span></b><span data-component="byoai-status">${escapeHtml(S.byoaiNone)}</span></div><span class="row__act"><a class="minibtn" data-nav href="/connecter">${escapeHtml(S.connect)}</a></span></div>
          <div class="row"><span class="row__ic">🔌</span><div class="row__main"><b>${escapeHtml(S.mcpT)} <span class="live">${escapeHtml(S.live)}</span></b><span>${escapeHtml(S.mcpD)}</span></div><span class="row__act"><a class="minibtn" data-nav href="/connecter">${escapeHtml(S.configure)}</a></span></div>
          <div class="row"><span class="row__ic">📦</span><div class="row__main"><b>${escapeHtml(S.forgejoT)}</b><span data-component="forgejo-status">${escapeHtml(S.forgejoNone)}</span></div><span class="row__act"><a class="minibtn" data-nav href="/welcome">${escapeHtml(S.see)}</a></span></div>
        </section>

        <section class="block" id="me-bill">
          <h2 class="block__h">${escapeHtml(S.billH)}</h2>
          <p class="block__sub">${escapeHtml(S.billSub)}</p>
          <div class="row"><span class="row__ic">🧾</span><div class="row__main" style="flex:1"><b>${escapeHtml(S.billHist)} <span class="live">${escapeHtml(S.live)}</span></b><div class="bill-history" data-component="bill-history">${escapeHtml(S.billLoading)}</div></div></div>
          <div class="row" data-component="cloud-sub-row"><span class="row__ic">☁️</span><div class="row__main"><b>${escapeHtml(S.billCloud)}</b><span data-component="cloud-sub-state">${escapeHtml(S.billCloudD)}</span></div><span class="row__act" data-component="cloud-sub-act"><span class="bientot">${escapeHtml(S.bientot)}</span></span></div>
        </section>

        <section class="block" id="me-sec">
          <h2 class="block__h">${escapeHtml(S.secH)}</h2>
          <p class="block__sub">${escapeHtml(S.secSub)}</p>
          <div class="row"><span class="row__ic">✉️</span><div class="row__main"><b>${escapeHtml(S.secMagic)} <span class="live">${escapeHtml(S.live)}</span></b><span>${escapeHtml(S.secMagicD(email))}</span></div><span class="row__act"><span class="bientot">${escapeHtml(S.bientot)}</span></span></div>
          <div class="row"><span class="row__ic">💻</span><div class="row__main"><b>${escapeHtml(S.secSess)}</b><span>${escapeHtml(S.secSessD)}</span></div><span class="row__act">${opts.onSignOut ? `<button class="minibtn" data-component="signout-all">${escapeHtml(S.signOutAll)}</button>` : ''}</span></div>
          <div class="row"><span class="row__ic">🔐</span><div class="row__main"><b>${escapeHtml(S.sec2fa)}</b><span>${escapeHtml(S.sec2faD)}</span></div><span class="row__act"><span class="bientot">${escapeHtml(S.bientot)}</span></span></div>
        </section>

        <section class="block" id="me-prefs">
          <h2 class="block__h">${escapeHtml(S.prefsH)}</h2>
          <div class="row"><span class="row__ic">🌐</span><div class="row__main"><b>${escapeHtml(S.prefLang)} <span class="live">${escapeHtml(S.live)}</span></b><span>${escapeHtml(S.prefLangD)} ${locale === 'en' ? 'English' : 'français'}</span></div><span class="row__act"><span class="miniseg" data-component="lang-seg"><button data-lang="fr" aria-pressed="${locale !== 'en'}">FR</button><button data-lang="en" aria-pressed="${locale === 'en'}">EN</button></span></span></div>
          <div class="row"><span class="row__ic">🎨</span><div class="row__main"><b>${escapeHtml(S.prefTheme)}</b><span>${escapeHtml(S.prefThemeD)}</span></div><span class="row__act"><span class="bientot">${escapeHtml(S.themeSoon)}</span></span></div>
          <div class="row"><span class="row__ic">🔔</span><div class="row__main"><b>${escapeHtml(S.prefNotif)}</b><span>${escapeHtml(S.prefNotifD)}</span></div><span class="row__act"><span class="bientot">${escapeHtml(S.bientot)}</span></span></div>
        </section>

        <section class="block danger" id="me-data">
          <h2 class="block__h">${escapeHtml(S.dataH)}</h2>
          <p class="block__sub">${escapeHtml(S.dataSub)}</p>
          <div class="row"><span class="row__ic">📤</span><div class="row__main"><b>${escapeHtml(S.dataExport)}</b><span>${escapeHtml(S.dataExportD)}</span></div><span class="row__act"><span class="bientot">${escapeHtml(S.bientot)}</span></span></div>
          <div class="row"><span class="row__ic">🗑️</span><div class="row__main"><b>${escapeHtml(S.dataDelete)}</b><span>${escapeHtml(S.dataDeleteD)}</span></div><span class="row__act"><span class="bientot">${escapeHtml(S.bientot)}</span></span></div>
        </section>

        <section class="block" id="me-help">
          <h2 class="block__h">${escapeHtml(S.helpH)}</h2>
          <div class="row"><span class="row__ic">📚</span><div class="row__main"><b>${escapeHtml(S.helpGuide)}</b><span>${escapeHtml(S.helpGuideD)}</span></div><span class="row__act"><a class="minibtn" data-nav href="/exemples">${escapeHtml(S.open)}</a></span></div>
          <div class="row"><span class="row__ic">💬</span><div class="row__main"><b>${escapeHtml(S.helpSupport)}</b><span>${escapeHtml(S.helpSupportD)}</span></div><span class="row__act"><a class="minibtn" href="mailto:contact@myconciergerie.fr">${escapeHtml(S.contact)}</a></span></div>
          <div class="row"><span class="row__ic">🏛️</span><div class="row__main"><b>${escapeHtml(S.helpEnt)}</b><span>${escapeHtml(S.helpEntD)}</span></div><span class="row__act"><a class="minibtn" data-nav href="/tarifs">${escapeHtml(S.talk)}</a></span></div>
        </section>
      </div>
    </div>
    <p class="me-foot">${escapeHtml(S.foot)}</p>
  `;
  frag.appendChild(main);
  frag.appendChild(renderFooter({ onSignOut: opts.onSignOut }));

  // ── wire : sign-out (identity + "déconnecter partout") ──
  if (opts.onSignOut) {
    const so = opts.onSignOut;
    main.querySelector<HTMLButtonElement>('[data-component="signout"]')?.addEventListener('click', () => void so());
    main.querySelector<HTMLButtonElement>('[data-component="signout-all"]')?.addEventListener('click', () => void so());
  }

  // ── wire : CP-2 checkout CTAs (Mollie, test mode) — one-time SKUs only ──
  main.querySelectorAll<HTMLButtonElement>('[data-checkout]').forEach((btn) => {
    wireCheckoutCta(btn, btn.getAttribute('data-checkout')!);
  });

  // ── wire : CP-3 Cloud subscribe CTA (only present when cloudEnabled) ──
  main.querySelector<HTMLButtonElement>('[data-checkout-cloud]')?.addEventListener('click', () => {
    void (async () => {
      const r = await subscribeCloud();
      if (!r.ok && r.reason === 'need_auth') window.dispatchEvent(new CustomEvent('genesis:need-auth'));
      else if (!r.ok && r.reason === 'error') console.warn('[compte] cloud subscribe failed');
    })();
  });

  // ── wire : post-checkout success banner dismiss ──
  main.querySelector<HTMLButtonElement>('[data-component="purchase-banner-x"]')
    ?.addEventListener('click', () => {
      main.querySelector<HTMLElement>('[data-component="purchase-banner"]')?.remove();
    });

  // ── load : purchase history (Facturation) from public.payments ──
  const skuLabel = (sku: string): string =>
    sku === 'pack_web' ? S.skuPackWeb
      : sku === 'pack_terminal' ? S.skuPackTerminal
      : sku === 'desktop_lifetime' ? S.skuDesktop
      : sku;
  void listPayments().then((payments) => {
    const host = main.querySelector<HTMLElement>('[data-component="bill-history"]');
    if (!host) return;
    if (payments.length === 0) { host.textContent = S.billEmpty; return; }
    const fmtDate = (iso: string) => {
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(locale === 'en' ? 'en' : 'fr');
    };
    host.innerHTML = payments.map((pmt) =>
      `<div class="bill-row"><span class="bill-row__date">${escapeHtml(fmtDate(pmt.createdAt))}</span>` +
      `<span class="bill-row__sku">${escapeHtml(skuLabel(pmt.sku))}</span>` +
      `<span class="bill-row__amt">${escapeHtml(pmt.amountEur)} €</span>` +
      `<span class="bill-row__status">${escapeHtml(pmt.status === 'paid' ? S.statusPaid : pmt.status)}</span></div>`,
    ).join('');
  }).catch((err) => console.warn('[compte] listPayments failed:', err));

  // ── wire : in-page FR/EN segments → reuse the header's global locale toggle ──
  main.querySelectorAll<HTMLButtonElement>('[data-component="lang-seg"] button[data-lang]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const lang = btn.getAttribute('data-lang');
      if (lang === locale) return;
      document.querySelector<HTMLButtonElement>(`.site-header [data-component="locale-toggle"] button[data-locale="${lang}"]`)?.click();
    });
  });

  // ── async : entitlement → gauge + plan chip ──
  void getEntitlement().then((e) => {
    const allowed = Math.max(0, e.freeQuota + e.purchasedCredits);
    const numEl = main.querySelector<HTMLElement>('[data-component="gauge-num"]');
    const fillEl = main.querySelector<HTMLElement>('[data-component="gauge-fill"]');
    main.querySelector<HTMLElement>('[data-component="leg-offered"]')!.textContent = String(e.freeQuota);
    main.querySelector<HTMLElement>('[data-component="leg-bought"]')!.textContent = String(e.purchasedCredits);
    main.querySelector<HTMLElement>('[data-component="leg-used"]')!.textContent = String(e.ideasConsumed);
    const pct = allowed > 0 ? Math.min(100, Math.round((e.remaining / allowed) * 100)) : 0;
    if (fillEl) fillEl.style.setProperty('--target', pct + '%');
    // count-up 0 → remaining
    if (numEl) {
      const target = Math.max(0, e.remaining);
      const t0 = performance.now(), dur = 850;
      const step = (t: number): void => {
        const k = Math.min(1, (t - t0) / dur);
        numEl.textContent = String(Math.round(k * target));
        if (k < 1) requestAnimationFrame(step);
      };
      setTimeout(() => requestAnimationFrame(step), 350);
    }
    // plan chip + header pill
    const planEl = main.querySelector<HTMLElement>('[data-component="plan-chip"]');
    if (planEl) {
      planEl.textContent = e.cloudStatus === 'active' ? 'Cloud' : e.desktopLicense ? (locale === 'en' ? 'Desktop · for life' : 'Desktop à vie') : S.heroPlan;
    }
    const pillN = document.querySelector<HTMLElement>('.site-espace-pill [data-component="pill-ideas"]');
    if (pillN) pillN.textContent = String(Math.max(0, e.remaining));
    // Facturation : Cloud subscription row reflects the live entitlement.
    if (e.cloudStatus === 'active') {
      const stateEl = main.querySelector<HTMLElement>('[data-component="cloud-sub-state"]');
      const actEl = main.querySelector<HTMLElement>('[data-component="cloud-sub-act"]');
      if (stateEl) {
        const until = e.cloudUntil ? new Date(e.cloudUntil).toLocaleDateString(locale === 'en' ? 'en' : 'fr') : '';
        stateEl.textContent = `${S.cloudActive}${until ? ` — ${S.cloudUntilLabel} ${until}` : ''}`;
      }
      if (actEl) {
        actEl.innerHTML = `<button type="button" class="minibtn" data-component="cloud-cancel">${escapeHtml(S.resilier)}</button>`;
        actEl.querySelector<HTMLButtonElement>('[data-component="cloud-cancel"]')?.addEventListener('click', () => {
          if (!window.confirm(S.resilierConfirm)) return;
          void cancelCloud().then((r) => { if (r.ok) window.location.reload(); });
        });
      }
    }
  }).catch((err) => console.warn('[compte] getEntitlement failed:', err));

  // ── async : projects → grid + count + forgejo status ──
  void listProjects().then((rows) => {
    const grid = main.querySelector<HTMLElement>('[data-component="proj-grid"]');
    const count = main.querySelector<HTMLElement>('[data-component="proj-count"]');
    if (count) count.textContent = rows.length ? S.projCount(rows.length) : S.projEmpty;
    if (grid) {
      grid.innerHTML = rows.slice(0, 6).map((r) => {
        const ver = (r.current_version_number ?? 1) > 1 ? `v${r.current_version_number}` : 'v1';
        return `<div class="proj"><b>${escapeHtml(r.title || '—')}</b><span>${escapeHtml((r.stack || '').split('+')[0].trim() || 'projet')}</span><span class="proj__v">${escapeHtml(ver)}</span></div>`;
      }).join('');
    }
    const pushed = rows.filter((r) => r.forgejo_repo_url).length;
    const fg = main.querySelector<HTMLElement>('[data-component="forgejo-status"]');
    if (fg) fg.textContent = pushed ? S.forgejoD(pushed) : S.forgejoNone;
  }).catch((err) => console.warn('[compte] listProjects failed:', err));

  // ── async : BYOAI provider status ──
  void getUserAiProvider().then((p) => {
    if (!p) return;
    const el = main.querySelector<HTMLElement>('[data-component="byoai-status"]');
    if (el) el.textContent = S.byoaiSet((p as { provider_kind?: string }).provider_kind ?? 'BYOAI');
  }).catch(() => { /* keine — leave the "aucune clé" default */ });

  return frag;
}
